import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../../component/layout/Layout";
import {
  getMyAccountStatus,
  applyMoment,
  registerChannel,
  type MomentAccountStatus,
} from "../api/momentApi";
import guideChannelImg from "../image/moment_search_id_img.png";
import guideAdminImg from "../image/moment_manager_invite.png";
import KakaoMomentTermsModal from "../components/KakaoMomentTermsModal";

export default function MomentOnboardingPage() {
  return (
    <Layout>
      <MomentOnboardingContent />
    </Layout>
  );
}

function MomentOnboardingContent() {
  const navigate = useNavigate();
  const [account, setAccount] = useState<MomentAccountStatus | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [channelSearchIdInput, setChannelSearchIdInput] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedImg, setSelectedImg] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [showTermsModal, setShowTermsModal] = useState(false);

  const loadStatus = async () => {
    try {
      const data = await getMyAccountStatus();
      setAccount(data ?? null);
    } catch {
      setAccount(null);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    if (!toastMsg) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setToastMsg("");
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [toastMsg]);

  const handleApply = async () => {
    setIsLoading(true);
    setErrorMsg("");
    try {
      const data = await applyMoment("Y");
      setAccount(data);
    } catch (e: unknown) {
      setErrorMsg(e instanceof Error ? e.message : "신청 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterChannel = async () => {
    if (!channelSearchIdInput.trim()) {
      alert("검색용 아이디를 입력해주세요.");
      return;
    }
    setIsLoading(true);
    setErrorMsg("");
    try {
      const data = await registerChannel(channelSearchIdInput.trim());
      setAccount(data);
    } catch (e: unknown) {
      setErrorMsg(e instanceof Error ? e.message : "채널 등록 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyInviteEmail = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteAdminEmail);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = inviteAdminEmail;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setToastType("success");
      setToastMsg("이메일 주소가 복사되었습니다.");
    } catch {
      setToastType("error");
      setToastMsg("이메일 주소 복사에 실패했습니다.");
    }
  };

  if (account === undefined) {
    return (
      <div className="moment-page tw-min-h-[calc(100vh-200px)] tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-sm">
        불러오는 중...
      </div>
    );
  }

  const currentStep = !account ? 1 : account.status === "ACTIVE" ? 3 : 2;
  const inviteAdminEmail = account?.inviteAdminEmail?.trim() || "tf@biztalk.co.kr";
  const publicGuideChannelImg = "/moment/moment_search_id_img.png";
  const publicGuideAdminImg = "/moment/moment_manager_invite.png";

  return (
    <div className="moment-page tw-min-h-[calc(100vh-200px)] tw-bg-gray-50 tw-py-10">
      {toastMsg && (
        <div className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-pointer-events-none">
          <div
            className={`tw-rounded-xl tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-shadow-lg ${
              toastType === "success"
                ? "tw-bg-slate-900 tw-text-white"
                : "tw-bg-red-500 tw-text-white"
            }`}
          >
            {toastMsg}
          </div>
        </div>
      )}
      <div className="tw-max-w-[680px] tw-mx-auto tw-px-6">

        {/* 헤더 */}
        <div className="tw-mb-8">
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-800">카카오 모먼트 연동하기</h1>
          <p className="tw-text-sm tw-text-gray-500 tw-mt-1">
            아래 단계를 순서대로 진행하시면 서비스를 이용할 수 있습니다.
          </p>
        </div>

        {/* 사전 안내 */}
        <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-2xl tw-p-4 tw-mb-6 tw-flex tw-items-start tw-gap-3">
          <span className="tw-text-[#5D87FF] tw-font-bold">ℹ️</span>
          <div>
            <p className="tw-text-sm tw-text-gray-800 tw-font-medium">사전 안내</p>
            <p className="tw-text-sm tw-text-gray-700 tw-mt-1">
              해당 기능은 <span className="tw-font-semibold">카카오 비즈니스 인증이 완료된 채널</span>이 있어야 이용할 수 있습니다.
            </p>
          </div>
        </div>

        {/* 단계 인디케이터 */}
        <StepIndicator currentStep={currentStep} />

        {/* 에러 메시지 */}
        {errorMsg && (
          <div className="tw-mt-5 tw-px-4 tw-py-3 tw-rounded-lg tw-text-sm tw-bg-red-50 tw-text-red-700 tw-border tw-border-red-200">
            {errorMsg}
          </div>
        )}

        <div className="tw-mt-6 tw-space-y-4">

          {/* STEP 1: 서비스 신청 */}
          {account === null ? (
            /* 미신청 상태 */
            <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-[#5D87FF]/40 tw-p-6">
              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                <StepBadge num={1} active />
                <div>
                  <div className="tw-font-semibold tw-text-gray-800">서비스 신청</div>
                </div>
              </div>
              <div className="tw-pl-10">
                <button
                  onClick={() => setShowTermsModal(true)}
                  disabled={isLoading}
                  className="tw-w-full tw-py-3 tw-bg-[#5D87FF] tw-text-white tw-rounded-xl tw-font-bold tw-text-sm tw-border-0 disabled:tw-opacity-50"
                >
                  {isLoading ? "신청 중..." : "서비스 신청하기"}
                </button>
              </div>
            </div>
          ) : (
            /* 신청 완료 카드 */
            <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-green-200 tw-p-6">
              <div className="tw-flex tw-items-center tw-gap-3">
                <StepBadge num={1} done />
                <div className="tw-flex-1">
                  <div className="tw-font-semibold tw-text-green-700">서비스 신청 완료</div>
                </div>
                <span className="tw-text-xs tw-text-green-600 tw-font-medium tw-bg-green-100 tw-px-2 tw-py-0.5 tw-rounded-full">완료</span>
              </div>
            </div>
          )}

          {/* STEP 2: 채널 연동 */}
          {account !== null && account.status !== "ACTIVE" && (
            <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-[#5D87FF]/40 tw-p-6">
              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                <StepBadge num={2} active />
                <div>
                  <div className="tw-font-semibold tw-text-gray-800">채널 연동</div>
                  <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5">
                    카카오톡 채널 관리자를 초대한 후 채널명을 등록합니다.
                  </div>
                </div>
              </div>

              <div className="tw-pl-10 tw-space-y-4">
                {/* 채널명 등록 or 대기 */}
                {!account.channelSearchId ? (
                  <>
                    {/* 관리자 초대 안내 */}
                    <div>
                      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-text-sm tw-text-gray-700 tw-mb-4">
                        <div className="tw-flex tw-justify-between tw-items-center tw-gap-4">
                          <div>
                            <p className="tw-text-xs tw-text-gray-500 tw-mb-1">초대 계정 주소</p>
                            <p className="tw-text-base tw-font-bold tw-text-[#5D87FF] tw-select-all">{inviteAdminEmail}</p>
                          </div>
                          <button
                            onClick={handleCopyInviteEmail}
                            className="tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-px-2 tw-py-1 tw-rounded hover:tw-bg-gray-50"
                          >
                            복사하기
                          </button>
                        </div>
                        <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200 tw-text-xs">
                          경로: 채널 → 관리자 → <strong>+ 관리자 초대</strong> 클릭
                        </div>
                      </div>

                      <GuideImageCard
                        src={publicGuideAdminImg}
                        fallbackSrc={guideAdminImg}
                        alt="관리자 초대 가이드"
                        caption="* 노란색 '+ 관리자 초대' 버튼을 눌러 이메일을 입력해 주세요."
                        onClick={setSelectedImg}
                      />

                      <a
                        href="https://business.kakao.com/_jxcupG/dashboard"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-border-0 tw-block tw-text-center tw-bg-[#5D87FF] tw-text-white tw-py-3 tw-rounded-lg hover:tw-opacity-90 tw-no-underline tw-text-sm tw-font-bold"
                      >
                        카카오 비즈니스 센터 바로가기
                      </a>
                    </div>

                    {/* 채널 검색용 아이디 입력 */}
                    <div>
                      <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-text-sm tw-text-gray-700 tw-mb-4">
                        <p className="tw-font-medium tw-mb-2">확인 경로</p>
                        <p className="tw-text-gray-500">
                          카카오 비즈니스 → 로그인 → 채널 → 채널 정보 → 검색용 아이디
                        </p>
                      </div>

                      <GuideImageCard
                        src={publicGuideChannelImg}
                        fallbackSrc={guideChannelImg}
                        alt="채널 정보 확인 가이드"
                        caption="* 채널 정보 메뉴에서 '검색용 아이디' 항목을 복사하여 사용하세요."
                        onClick={setSelectedImg}
                      />

                      <label className="tw-text-xs tw-text-gray-500 tw-block tw-mb-1">카카오톡 채널 검색용 아이디</label>
                      <div className="tw-flex tw-gap-2">
                        <input
                          type="text"
                          placeholder="예: biztalk"
                          value={channelSearchIdInput}
                          onChange={(e) => setChannelSearchIdInput(e.target.value)}
                          className="tw-flex-1 tw-border tw-border-gray-300 tw-rounded-xl tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-[#5D87FF]"
                        />
                        <button
                          onClick={handleRegisterChannel}
                          disabled={isLoading}
                          className="tw-border-0 tw-flex-shrink-0 tw-w-44 tw-whitespace-nowrap tw-text-center tw-py-2 tw-bg-[#5D87FF] tw-text-white tw-rounded-xl tw-font-medium tw-text-sm disabled:tw-opacity-50"
                        >
                          {isLoading ? "등록 중..." : "검색용 아이디 등록"}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-xl tw-p-4 tw-space-y-1.5">
                    <p className="tw-text-sm tw-text-blue-800 tw-font-semibold">승인 대기 중</p>
                    <p className="tw-text-sm tw-text-blue-700">
                      채널 <strong>{account.channelSearchId}</strong>이 등록되었습니다.
                      관리자 확인 및 광고계정 생성까지 영업일 기준 1~2일 소요됩니다.
                    </p>
                    <p className="tw-text-xs tw-text-blue-600">
                      승인 이후 가입하신 휴대폰 번호로 알림톡이 발송됩니다.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 2: 채널 연동 완료 (ACTIVE) */}
          {account?.status === "ACTIVE" && (
            <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-green-200 tw-p-6">
              <div className="tw-flex tw-items-center tw-gap-3">
                <StepBadge num={2} done />
                <div className="tw-flex-1">
                  <div className="tw-font-semibold tw-text-green-700">채널 연동 완료</div>
                  <div className="tw-text-sm tw-text-gray-500 tw-mt-0.5">
                    채널: <span className="tw-font-semibold tw-text-gray-700">{account.channelName ?? account.channelSearchId}</span>
                  </div>
                </div>
                <span className="tw-text-xs tw-text-green-600 tw-font-medium tw-bg-green-100 tw-px-2 tw-py-0.5 tw-rounded-full">완료</span>
              </div>
            </div>
          )}

          {/* STEP 3: 서비스 이용 */}
          {account?.status === "ACTIVE" ? (
            <div className="tw-bg-[#5D87FF]/5 tw-rounded-2xl tw-border tw-border-[#5D87FF]/30 tw-p-6">
              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                <StepBadge num={3} active />
                <div className="tw-font-semibold tw-text-gray-800">서비스 이용 중</div>
              </div>
              <div className="tw-pl-10 tw-flex tw-gap-3">
                <button
                  onClick={() => navigate("/moment/create")}
                  className="tw-flex-1 tw-py-2.5 tw-bg-[#5D87FF] tw-text-white tw-rounded-xl tw-font-bold tw-text-sm tw-border-0 hover:tw-bg-[#4c74ef] tw-transition-colors"
                >
                  메시지 광고 등록
                </button>
                <button
                  onClick={() => navigate("/moment/list")}
                  className="tw-flex-1 tw-py-2.5 tw-bg-blue-50 tw-border tw-border-[#5D87FF]/25 tw-text-[#3558c9] tw-rounded-xl tw-font-bold tw-text-sm hover:tw-bg-blue-100 tw-transition-colors"
                >
                  메시지 광고 관리
                </button>
              </div>
            </div>
          ) : (
            <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-p-6 tw-opacity-50">
              <div className="tw-flex tw-items-center tw-gap-3">
                <StepBadge num={3} />
                <div className="tw-font-semibold tw-text-gray-400">서비스 이용</div>
              </div>
            </div>
          )}

        </div>

        {selectedImg && (
          <div
            className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-bg-black/90 tw-p-4"
            onClick={() => setSelectedImg(null)}
          >
            <div className="tw-relative tw-w-full tw-max-w-6xl">
              <img
                src={selectedImg}
                alt="가이드 확대보기"
                className="tw-h-auto tw-w-full tw-rounded-lg tw-shadow-2xl"
              />
              <button
                className="tw-absolute tw-right-0 tw-top-[-36px] tw-border-0 tw-bg-transparent tw-p-0 tw-text-base tw-font-medium tw-text-white hover:tw-text-gray-300"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImg(null);
                }}
              >
                닫기
              </button>
            </div>
          </div>
        )}
      </div>
      <KakaoMomentTermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAgree={async () => {
          setShowTermsModal(false);
          await handleApply();
        }}
      />
    </div>
  );
}

/* ── 단계 인디케이터 ── */
function StepIndicator({ currentStep }: { currentStep: number }) {
  const steps = ["서비스 신청", "채널 연동", "이용 중"];

  return (
    <div className="tw-flex tw-items-center">
      {steps.map((label, i) => {
        const num = i + 1;
        const done = num < currentStep;
        const active = num === currentStep;
        return (
          <div key={label} className="tw-flex tw-items-center tw-flex-1 last:tw-flex-none">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-1.5">
              <div className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-sm tw-font-bold ${
                done ? "tw-bg-[#5D87FF] tw-text-white"
                  : active ? "tw-bg-[#5D87FF] tw-text-white tw-ring-4 tw-ring-[#5D87FF]/20"
                  : "tw-bg-gray-200 tw-text-gray-400"
              }`}>
                {done ? "✓" : num}
              </div>
              <span className={`tw-text-xs tw-whitespace-nowrap ${active ? "tw-text-[#5D87FF] tw-font-semibold" : "tw-text-gray-400"}`}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`tw-h-px tw-flex-1 tw-mx-2 tw-mb-5 ${done ? "tw-bg-[#5D87FF]" : "tw-bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── 단계 뱃지 ── */
function StepBadge({ num, active = false, done = false }: { num: number; active?: boolean; done?: boolean }) {
  return (
    <div className={`tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold tw-flex-shrink-0 ${
      done ? "tw-bg-green-500 tw-text-white"
        : active ? "tw-bg-[#5D87FF] tw-text-white"
        : "tw-bg-gray-200 tw-text-gray-400"
    }`}>
      {done ? "✓" : num}
    </div>
  );
}

function GuideImageCard({
  src,
  fallbackSrc,
  alt,
  caption,
  onClick,
}: {
  src: string;
  fallbackSrc: string;
  alt: string;
  caption: string;
  onClick: (src: string) => void;
}) {
  const [imgSrc, setImgSrc] = useState(src);

  return (
    <div
      className="tw-relative tw-group tw-cursor-zoom-in tw-mb-6 tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm"
      onClick={() => onClick(imgSrc)}
    >
      <img
        src={imgSrc}
        alt={alt}
        className="tw-h-auto tw-w-full tw-transition-transform tw-duration-300 group-hover:tw-scale-[1.02]"
        onError={() => {
          if (imgSrc !== fallbackSrc) {
            setImgSrc(fallbackSrc);
          }
        }}
      />
      <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100">
        <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-black/70 tw-px-5 tw-py-2.5 tw-text-sm tw-font-bold tw-text-white tw-shadow-lg">
          <span>가이드 확대보기</span>
        </div>
      </div>
      <div className="tw-bg-gray-50 tw-p-3 tw-text-xs tw-italic tw-text-gray-500">
        {caption}
      </div>
    </div>
  );
}
